import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BoobleModalService } from 'booble-modal';
import { CountdownComponent } from 'ngx-countdown';
import {
  Subscription,
  map,
  tap,
  Observable,
  switchMap,
  interval,
  startWith,
} from 'rxjs';
import { CorporateAccountDto } from 'src/app/domain/models/dto/corporate-account.dto';
import { CreateQuotationDto } from 'src/app/domain/models/dto/create-quotation.dto';
import { RateDto } from 'src/app/domain/models/dto/rate.dto';
import { AuthenticationService } from 'src/app/shared/services';
import { CorporateAccountService } from 'src/app/shared/services/corporate-account.service';
import { LINK_REFINITV } from 'src/environments/environment';
import { QuotationConfirmModalComponent } from '../quotation-confirm-modal/quotation-confirm-modal.component';
import { BrokerHubService } from 'src/app/shared/services/broker-hub.service';
import { OrdersService } from 'src/app/shared/services/orders.service';
import { NotifierService } from 'angular-notifier';
import { MerchantService } from 'src/app/core/merchant.service';
import { TenantGeneralService } from 'src/app/core/tenant-general.service';

const RATE_VALID_TIME = 30000;

export type Calculated = {
  iofPercentage: number;
  irPercentage: number;
  commercial: number;
  totalCommercial: number;
  totalCommercialIof: number;
  residualIr: number;
  totalIr: number;
  totalIof: number;
  vet: number;
  tariff: number;
  amountMN: number;
  spread: number;
};

@Component({
  selector: 'app-quotation-modal',
  templateUrl: './quotation-modal.component.html',
  styleUrls: ['./quotation-modal.component.scss'],
})
export class QuotationModalComponent implements OnInit, OnDestroy {
  boobleModalData: any;
  company: CorporateAccountDto;
  companyId: number;
  showDetails = false;
  rate: number;
  selectedRate: RateDto;
  testing: boolean;
  currentHour: number;

  calculator: any = {};

  quotationAvailability$?: Observable<boolean>;
  taxIof?: number;
  taxId?: number;
  taxIr: number;
  isMobile: boolean;
  rates: RateDto[] = [];

  link = LINK_REFINITV;
  subscription: Subscription;
  brokerSubscription?: Subscription;
  token: string;
  spreadBanking: boolean = false;
  userSpread: number;
  userTariff: number;
  merchant: string = '';
  isWarrenGlobal: boolean = false;

  @ViewChild('cd', { static: false }) private countdown: CountdownComponent;
  calculated: Calculated;

  private readonly notifier: NotifierService;

  get validHour() {
    const currentDateUTC = new Date();
    const brasilTimeZoneOffset = -3;
    const currentOffset = currentDateUTC.getTimezoneOffset() / 60;
    const diffInHours = brasilTimeZoneOffset + currentOffset;
    const currentDateBrazil = new Date(
      currentDateUTC.getTime() + diffInHours * 60 * 60 * 1000
    );

    this.currentHour = currentDateBrazil.getHours();
    return this.currentHour >= 9 && this.currentHour < 17;
  }

  constructor(
    private authService: AuthenticationService,
    private orderService: OrdersService,
    private companyService: CorporateAccountService,
    public modalService: BoobleModalService,
    private brokerHub: BrokerHubService,
    notifierService: NotifierService,
    private tenantService: TenantGeneralService,
    private merchantService: MerchantService
  ) {
    this.notifier = notifierService;
    this.isMobile = window.innerWidth < 600;
    this.companyId = +this.authService.getCompanyId();
    this.testing = false;
  }

  ngOnInit() {
    this.merchant = this.merchantService.getUserMerchant();
    sessionStorage.setItem('hide-toast', 'true');

    this.isWarrenGlobal =
      this.merchant == this.tenantService.WARREN_GLOBAL_NAME;

    this.companyService.get().subscribe((getResult) => {
      this.company = getResult.data;
      this.spreadBanking = getResult.data?.businessPartner?.spreadBanking;

      this.userSpread = getResult.data?.spread;
      this.userTariff = getResult.data?.tariff;

      this.brokerSubscription = interval(RATE_VALID_TIME)
        .pipe(
          startWith(0),
          switchMap(() =>
            this.boobleModalData.orderId
              ? this.brokerHub.newCompeletedRate(this.boobleModalData?.orderId)
              : this.brokerHub.getRateQuotation(
                  this.boobleModalData.currency.id,
                  this.boobleModalData.finality?.id,
                  this.boobleModalData.country?.id,
                  this.boobleModalData.amount,
                  this.boobleModalData.orderType,
                  this.userSpread,
                  this.userTariff
                )
          ),
          map((res) => res.data),
          tap(() => this.countdown?.restart())
        )
        .subscribe((rate) => {
          this.rate = rate.rate;
          this.token = rate.token;
          this.taxId = rate.taxId;
          this.taxIof = rate.iofPercentage;
          this.taxIr = rate.irPercentage;
          this.boobleModalData.amount = rate.amount;

          this.calculated = {
            iofPercentage: rate.iofPercentage,
            irPercentage: rate.irPercentage,
            commercial: rate.comercial,
            totalCommercial: rate.total,
            totalCommercialIof: 0,
            residualIr: 0,
            totalIr: rate.ir,
            totalIof: rate.iof,
            vet: rate.vet,
            tariff: rate.tariff,
            amountMN: rate.total,
            spread: rate.spread,
          };

          this.calculator = {
            test: this.boobleModalData.discount ?? 0,
          };
        });
    });

    this.quotationAvailability$ = this.orderService
      .checkQuotationAvailability()
      .pipe(map((response) => response.data));
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.brokerSubscription?.unsubscribe();
    sessionStorage.removeItem('hide-toast');
  }

  formatIOF(value: number): string {
    const [intPart, decimalPart = ''] = value.toString().split('.');
    const truncated = decimalPart.slice(0, 2).padEnd(2, '0');
    return `${intPart}.${truncated}`;
  }

  closeModal() {
    this.modalService.dismiss(null);
  }

  openQuotationConfirmModal(quotation: any) {
    this.modalService.present(QuotationConfirmModalComponent, quotation);
  }

  confirm() {
    const hasOrderId = !!this.boobleModalData.orderId;

    if (hasOrderId && this.validHour) {
      if (this.testing) {
        const quotation = {
          orderId: this.boobleModalData.orderId,
          quotationData: new CreateQuotationDto(
            241,
            this.boobleModalData.amount,
            this.boobleModalData.amount * 5.25792306,
            5.17792306,
            5.0086,
            5.058686000000001,
            0,
            this.token
          ),
          order: this.boobleModalData.order,
        };
        this.openQuotationConfirmModal(quotation);
      } else {
        const quotation = {
          orderId: this.boobleModalData.orderId,
          quotationData: new CreateQuotationDto(
            this.taxId ?? 0,
            this.boobleModalData.amount,
            this.calculated.amountMN,
            this.calculated.vet,
            this.rate,
            this.calculated.commercial,
            0,
            this.token
          ),
          order: this.boobleModalData.order,
        };
        this.openQuotationConfirmModal(quotation);
      }
    } else if (hasOrderId && !this.validHour) {
      this.notifier.notify('error', 'Fechamento do câmbio indisponível.');
    } else {
      window.open(
        'https://api.whatsapp.com/send?phone=5511924832608&text=Ol%C3%A1,%20gostaria%20de%20fechar%20uma%20opera%C3%A7%C3%A3o%20de%20c%C3%A2mbio!',
        '_blank'
      );
    }
  }
}
